import React, { Children } from 'react'
import PropTypes from 'prop-types'
import { classNames, useGlobalId } from '@zambezi/sdk/common'
import Label from './Label'
import { getError, errorOptionsType, hintOptionsType } from './zambezi-helpers'

/**
 * @type {React.FunctionComponent<import('./interfaces').FieldGroupProps>}
 * @param {import('./interfaces').FieldGroupProps} props
 */
export default function FieldGroup ({
  label,
  children,
  value,
  error,
  errorOptions,
  className,
  hint,
  hintOptions,
  errorMessageShown, errorInline
}) {
  const globalId = useGlobalId('zb-fieldgroup-label-')
  const valueId = useGlobalId('zb-fieldgroup-value-')
  const errorId = useGlobalId('zb-fieldgroup-error-')
  const labelId = label ? globalId : null
  const labelOptions = {
    inputOptions: { 'aria-labelledby': labelId },
    error,
    errorOptions,
    hint,
    hintOptions,
    label
  }
  const childrenClassName = classNames('zb-fieldgroup-children', {
    'zb-is-error': !!error
  })
  const wrapperClassName = classNames(
    'zb-fieldgroup',
    'zb-control-wrap',
    className
  )
  const errorMessage = errorMessageShown(error)
    ? getError(error, errorOptions, errorId, errorInline)
    : null
  let describedBy = value ? valueId : ''
  if (errorMessage) {
    describedBy = describedBy ? describedBy + ' ' + errorId : errorId
  }

  return (
    <div
      className={wrapperClassName}
      role='group'
      aria-labelledby={labelId}
      aria-invalid={!!error}
      aria-describedby={describedBy}
    >
      {label ? <Label {...labelOptions}>{label}</Label> : null}
      {value ? (
        <div
          id={valueId}
          className={classNames('zb-control', { 'zb-is-error': !!error })}
        >
          {value}
        </div>
      ) : null}
      {Children.count(children) ? (
        <div className={childrenClassName}>{children}</div>
      ) : null}
      {errorMessage}
    </div>
  )
}

FieldGroup.defaultProps = {
  errorMessageShown: (error) => Boolean(error)
}

FieldGroup.propTypes = {
  className: PropTypes.string,
  error: PropTypes.any,
  errorMessageShown: PropTypes.func,
  errorOptions: errorOptionsType,
  label: PropTypes.node,
  value: PropTypes.node,
  hint: PropTypes.node,
  hintOptions: hintOptionsType,
  errorInline: PropTypes.bool
}
